// Customers (static demo pages/Customers.jsx, screen 2 / mock-up 02): partner
// staff pick a customer. One tile per customer account: plan, worst state
// and state bar of its sensors, stores, measuring points, users. Opening a
// tile switches to that customer and shows its overview.
import { useT } from "../i18n";
import { accounts, setAccount, tenantApi, tree } from "../lib/account";
import { useLoad } from "../lib/load";
import { go } from "../lib/nav";
import { useFleet } from "../data/fleet";
import { STATE_COLOR } from "../components/Nav.jsx";
import { worse, type St } from "../data/locations";
import { State } from "./common";

const COLORS = ["#0B4F6C", "#7A3E9D", "#C2410C", "#0F766E", "#B45309", "#1D4ED8", "#9D174D"];
const initials = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");

export default function Customers() {
  const t = useT();
  const f = useFleet(t.lang);
  const custs = tree.value.filter((r) => r.a.kind === "customer").map((r) => r.a);
  const extra = useLoad(async () => {
    const [users, plans] = await Promise.all([
      tenantApi<{ items: { roles: { account_id?: string }[] }[] }>("GET", "/users").then((r) => r.items, () => []),
      Promise.all(custs.map((c) => tenantApi<{ effective_template?: string | null; template?: string | null }>("GET", `/accounts/${c.id}/plan`).then((p) => [c.id, p.effective_template || p.template || ""] as const, () => [c.id, ""] as const))),
    ]);
    return { users, plans: Object.fromEntries(plans) };
  }, [custs.length]);
  const sub = accounts.value.find((a) => a.kind === "sub_partner" && !accounts.value.some((x) => x.kind === "root"));
  const under = (acc: string | null | undefined, c: string) => !!acc && (acc === c || !!accounts.value.find((a) => a.id === acc)?.ancestors?.includes(c));
  if (!f) return <div class="page"><State loading /></div>;
  return (
    <div class="page">
      <div class="page-h"><div><h1>{t("cust.title")}</h1><div class="sub">{sub ? t("cust.subInstaller", { name: sub.name }) : t("cust.sub")}</div></div></div>
      {custs.length === 0 && <p style="color:var(--muted)">{t("x.cust.none")}</p>}
      <div class="ctiles">
        {custs.map((c, i) => {
          const pts = f.points.filter((p) => under(p.owner, c.id));
          const counts: Record<St, number> = { crit: 0, alarm: 0, warn: 0, delayed: 0, ok: 0 };
          pts.forEach((p) => counts[p.st]++);
          const worst = pts.reduce<St>((w, p) => worse(w, p.st), "ok");
          const stores = new Set([...pts.map((p) => String(p.labels.site || "")).filter(Boolean), ...f.stores.filter((s) => under(s.account_id, c.id)).map((s) => s.value)]);
          const users = (extra.data?.users || []).filter((u) => u.roles.some((r) => under(r.account_id, c.id))).length;
          const plan = extra.data?.plans[c.id] || "";
          const tot = pts.length || 1;
          return (
            <button type="button" class="ctile" key={c.id} style={{ animationDelay: `${i * 0.12}s` }} onClick={() => { setAccount(c.id); go("/"); }}>
              <div class="row" style="justify-content:space-between;align-items:flex-start"><div class="row" style="gap:14px"><div class="clogo" style={{ background: COLORS[i % COLORS.length] }}>{initials(c.name)}</div><div><h3 style="margin:0;font-size:19px">{c.name}</h3><span class="mono" style="color:var(--muted);font-size:13px">{plan ? t("cust.plan", { p: plan, n: t("plan." + plan) }) : t("x.cust.noPlan")}</span></div></div>
                {pts.length === 0 ? <span class="st st-delayed">{t("st.none")}</span> : <span class={"st st-" + worst}>{worst === "ok" ? t("st.inRange") : t("st." + worst)}</span>}</div>
              <div class="bar" aria-hidden="true">{(["crit", "alarm", "warn", "delayed", "ok"] as St[]).map((k) => counts[k] ? <i key={k} style={{ width: `${(counts[k] / tot) * 100}%`, background: STATE_COLOR[k] }} /> : null)}</div>
              <div class="row" style="gap:22px">
                <div><div class="lbl">{t("cust.sites")}</div><b class="num" style="font-size:22px">{t.num(stores.size)}</b></div>
                <div><div class="lbl">{t("cust.points")}</div><b class="num" style="font-size:22px">{t.num(pts.length)}</b></div>
                <div><div class="lbl">{t("cust.users")}</div><b class="num" style="font-size:22px">{extra.data ? t.num(users) : "…"}</b></div>
              </div>
            </button>);
        })}
      </div>
      <p style="color:var(--muted);margin:0;max-width:760px">{t("cust.note")}</p>
    </div>
  );
}
